/**
 * 公司资料审批 mixin — 总驳回 / 驳回详情用公司组件；字段驳回用 CompanyFieldApprovalResults
 */
import { CompanyInfoService } from '@/_services/CompanyInfo.Service';
import { adminService } from '@/_services/admin.service';
import { PRODUCT_DISPLAY_STATUS, PRODUCT_LAUNCH_STATUS, resolveProductRowStatus, resolveProductLaunchStatus, PRODUCT_REVIEW_STATUS_I18N } from '@/_services/product.service';
import CompanyOverallRejectPanel from './CompanyOverallRejectPanel.vue';
import CompanyRejectDetailsModal from './CompanyRejectDetailsModal.vue';
import {
  isCompanyApprovalResultRadioSelected,
  resolveCompanyApprovalResultCodeForSave,
  isCompanyApprovalRejectedRow,
  resolveCompanyRejectReasonText,
  COMPANY_APPROVAL_RESULT_CODE_SELECTED,
  COMPANY_APPROVAL_RESULT_CODE_UNSELECTED,
  COMPANY_REJECT_USAGE_TYPES_BY_TAB,
} from './companyApprovalFieldMeta';
import { resolveApprovalRejectByActor } from '@/_helpers/approvalActor';
import { buildApprovalConfirmDialogBody } from '@/_helpers/approvalConfirmDialog';
import { SHOW_EDIT_SUPPLIER_REJECT_REASON, isVisibleApprovalRejectRow, shouldIncludeRejectHintsInTodo } from '@/constants/approvalRejectUi.js';

const PRODUCT_REVIEW_STATUS_I18N_LOCAL = PRODUCT_REVIEW_STATUS_I18N;

export default {
  components: {
    CompanyOverallRejectPanel,
    CompanyRejectDetailsModal,
  },
  props: {
    submitType: { type: String, default: 'edit' },
  },
  data() {
    return {
      approvalStatusCode: null,
      /** 公司上/下架：Company_LaunchStatus.IsLaunch */
      companyIsLaunch: 0,
      /** 打开审批页时记录的 ApprovalId，Finish 时与后端最新值比对 */
      loadedApprovalId: null,
      approvalRejectReason: '',
      approvalRejectBy: '',
      overallRejectReasonDraft: '',
      overallRejectTemplates: [],
      approvalContentResults: [],
      approvalContentResultDrafts: {},
      /** 上次落库的子项 ResultCode（勾选会改 live 行，需单独基线做 dirty 判断） */
      approvalContentResultCodeBaseline: {},
      fieldRejectErrorByKey: {},
      approvalRejectedTime: null,
      approvalActionTime: null,
      showRejectDetailsModal: false,
      statusLoaded: false,
      cdnURL: 'https://cdn.adsalecdn.com/EMP/Backend/',
      PRODUCT_DISPLAY_STATUS,
    };
  },
  computed: {
    fromAdmin() {
      return this.isAdminViewingSupplier;
    },
    isApprovalMode() {
      return String(this.submitType || 'edit').toLowerCase() === 'approval';
    },
    displayApprovalStatus() {
      return resolveProductRowStatus({ statusCode: this.approvalStatusCode });
    },
    launchStatus() {
      return resolveProductLaunchStatus({
        statusCode: this.approvalStatusCode,
        isLaunch: this.companyIsLaunch,
      });
    },
    launchStatusLabel() {
      return this.launchStatus === PRODUCT_LAUNCH_STATUS.online
        ? this.$t('lbl_EMPBE_Product_Online')
        : this.$t('lbl_EMPBE_Product_Offline');
    },
    launchStatusClass() {
      return this.launchStatus === PRODUCT_LAUNCH_STATUS.online
        ? 'is-launch-online'
        : 'is-launch-offline';
    },
    reviewStatusLabel() {
      const key = PRODUCT_REVIEW_STATUS_I18N_LOCAL[this.displayApprovalStatus]
        || PRODUCT_REVIEW_STATUS_I18N_LOCAL.toBeSubmitted;
      return this.$t(key);
    },
    reviewStatusClass() {
      const display = this.displayApprovalStatus;
      if (display === PRODUCT_DISPLAY_STATUS.live) return 'is-approved';
      if (display === PRODUCT_DISPLAY_STATUS.rejected) return 'is-rejected';
      if (display === PRODUCT_DISPLAY_STATUS.pending) return 'is-syncing';
      if (display === PRODUCT_DISPLAY_STATUS.pendingManualAudit) return 'is-manual';
      return 'is-not-submitted';
    },
    isApprovalPending() {
      return this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.pending
        || this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.pendingManualAudit;
    },
    isProductRejectedStatus() {
      return this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.rejected;
    },
    isCompanyRejectedStatus() {
      return this.isProductRejectedStatus;
    },
    /**
     * 驳回相关 UI：
     * - 已驳回(R)：显示
     * - 审批模式 + 已上架(A)：显示
     * - 审批中(P) / 人工待审批(PA)：不显示
     */
    showProductRejectPanels() {
      const s = this.displayApprovalStatus;
      if (s === PRODUCT_DISPLAY_STATUS.rejected) return true;
      if (this.isApprovalMode && s === PRODUCT_DISPLAY_STATUS.live) return true;
      return false;
    },
    showCompanyRejectPanels() {
      return this.showProductRejectPanels;
    },
    rejectedApprovalCount() {
      return (this.approvalContentResults || []).filter((row) =>
        isVisibleApprovalRejectRow(row, this),
      ).length;
    },
    hasSelectedFieldReject() {
      return (this.approvalContentResults || []).some((row) =>
        isCompanyApprovalRejectedRow(row));
    },
    /** 总驳回角标：已勾选子项数量 */
    selectedFieldRejectCount() {
      return (this.approvalContentResults || []).filter((row) =>
        isCompanyApprovalRejectedRow(row)
      ).length;
    },
    /** 横幅追加行：表头总驳回原因（有则显示；第二行默认说明始终单独展示） */
    rejectBannerHeaderReason() {
      return String(this.approvalRejectReason || '').trim();
    },
    /** TEMP：编辑/供应商已驳回页头横幅；审批模式不受影响 */
    showEditSupplierRejectBanner() {
      return SHOW_EDIT_SUPPLIER_REJECT_REASON
        && this.statusLoaded
        && !this.isApprovalMode
        && this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.rejected;
    },
    displayApprovalActionTime() {
      return this.approvalActionTime || this.approvalRejectedTime || '';
    },
  },
  watch: {
    approvalStatusCode: {
      handler(val) {
        if (this.fromAdmin && this.isApprovalMode) {
          this.$emit('approval-status-change', { statusCode: val });
        }
      },
      immediate: true,
    },
  },
  methods: {
    async loadCompanyApprovalIfNeeded() {
      if (!this.companyid) {
        this.statusLoaded = true;
        return;
      }
      try {
        await this.LoadApprovalStatus();
      } finally {
        this.statusLoaded = true;
      }
    },
    async LoadApprovalStatus() {
      if (!this.companyid) {
        this.resetApprovalState();
        return;
      }
      try {
        const statusRes = await CompanyInfoService.GetCompanyApprovalStatus({
          companyId: this.companyid,
        });
        if (statusRes?.isSuccessful && statusRes.data) {
          this.approvalStatusCode = statusRes.data.statusCode ?? statusRes.data.StatusCode ?? null;
          const rawLaunch = statusRes.data.isLaunch ?? statusRes.data.IsLaunch;
          this.companyIsLaunch = rawLaunch === true || rawLaunch === 1 || rawLaunch === '1' ? 1 : 0;
          this.loadedApprovalId = this.pickApprovalId(statusRes.data);
          this.approvalRejectReason = resolveCompanyRejectReasonText(
            statusRes.data.rejectReason || statusRes.data.RejectReason || '',
          );
          this.approvalRejectBy = resolveApprovalRejectByActor(statusRes.data);
          this.overallRejectReasonDraft = this.approvalRejectReason;
          this.approvalActionTime = this.resolveApprovalActionTimeFromStatus(statusRes.data);
        } else {
          this.approvalStatusCode = null;
          this.companyIsLaunch = 0;
          this.loadedApprovalId = null;
          this.approvalRejectReason = '';
          this.approvalRejectBy = '';
          this.overallRejectReasonDraft = '';
          this.approvalActionTime = null;
        }

        if (this.isApprovalMode) {
          this.loadOverallRejectTemplates();
        }

        const shouldLoadDetail = this.fromAdmin
          || this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.rejected
          || this.isApprovalPending
          || (this.isApprovalMode && this.displayApprovalStatus === PRODUCT_DISPLAY_STATUS.live);

        if (shouldLoadDetail) {
          const detailRes = await CompanyInfoService.GetCompanyApprovalDetail({
            empCompanyId: this.companyid,
          });
          if (detailRes?.isSuccessful && detailRes.data) {
            this.setApprovalContentResults(detailRes.data.approvalContentResults || []);
            this.approvalRejectedTime = this.resolveApprovalRejectedTime(detailRes.data);
            if (!this.approvalActionTime) {
              this.approvalActionTime = this.resolveApprovalActionTimeFromDetail(detailRes.data)
                || this.approvalRejectedTime;
            }
            if (!this.approvalStatusCode && detailRes.data.approvalHeader) {
              this.approvalStatusCode = detailRes.data.approvalHeader.statusCode;
            }
            const headerId = this.pickApprovalId(detailRes.data.approvalHeader);
            if (headerId) this.loadedApprovalId = headerId;
            if (!this.overallRejectReasonDraft) {
              const fromHistory = this.resolveOverallRejectFromHistory(detailRes.data);
              if (fromHistory.reason) {
                const reason = resolveCompanyRejectReasonText(fromHistory.reason);
                this.approvalRejectReason = reason;
                this.overallRejectReasonDraft = reason;
              }
              if (!this.approvalRejectBy && fromHistory.by) {
                this.approvalRejectBy = fromHistory.by;
              }
            }
          }
        } else {
          this.approvalContentResults = [];
          this.approvalContentResultDrafts = {};
          this.approvalContentResultCodeBaseline = {};
        }
        this.refreshTabExclamationMarks();
        this.$nextTick(() => {
          if (typeof this.refreshStickyLayoutOffsets === 'function') {
            this.refreshStickyLayoutOffsets();
          }
        });
      } catch (e) {
        console.error('LoadApprovalStatus', e);
      }
    },
    pickApprovalId(payload) {
      if (!payload || typeof payload !== 'object') return null;
      const id = Number(payload.approvalId ?? payload.ApprovalId ?? 0);
      return id > 0 ? id : null;
    },
    async ensureApprovalNotStale() {
      if (!this.fromAdmin || !this.isApprovalMode) return true;
      const openedId = Number(this.loadedApprovalId) || 0;
      if (openedId <= 0 || !this.companyid) return true;
      try {
        const statusRes = await CompanyInfoService.GetCompanyApprovalStatus({
          companyId: this.companyid,
        });
        const latestId = this.pickApprovalId(statusRes?.data) || 0;
        if (latestId > 0 && latestId !== openedId) {
          await this.showApprovalStaleReloadDialog();
          return false;
        }
      } catch (e) {
        // 拉取失败不阻断审批
      }
      return true;
    },
    async showApprovalStaleReloadDialog() {
      const res = await this.$swal({
        text: this.$t('lbl_EMPBE_ApprovalStale_Body'),
        showCancelButton: false,
        confirmButtonText: this.$t('lbl_EMPBE_Reload'),
        confirmButtonColor: '#7366FF',
        allowOutsideClick: false,
      });
      if (!res.isConfirmed) return;
      try {
        if (typeof this.loadData === 'function') {
          await this.loadData();
        }
        await this.LoadApprovalStatus();
      } catch (e) {
        // reload best-effort
      }
    },
    resetApprovalState() {
      this.approvalStatusCode = null;
      this.companyIsLaunch = 0;
      this.loadedApprovalId = null;
      this.approvalRejectReason = '';
      this.approvalRejectBy = '';
      this.overallRejectReasonDraft = '';
      this.approvalContentResults = [];
      this.approvalContentResultDrafts = {};
      this.approvalContentResultCodeBaseline = {};
      this.approvalRejectedTime = null;
      this.approvalActionTime = null;
      this.refreshTabExclamationMarks();
    },
    setApprovalContentResults(rows) {
      this.approvalContentResults = (rows || []).map((row) => ({
        ...row,
        usageId: row.usageId ?? row.UsageId ?? null,
        rejectReason: resolveCompanyRejectReasonText(
          row.rejectedReason || row.RejectedReason || row.rejectReason || '',
        ),
      }));
      this.approvalContentResultDrafts = {};
      this.syncApprovalResultCodeBaselineFromRows(this.approvalContentResults);
    },
    /** 状态接口：取最近审批/驳回时间（含 Header.UpdatedDate，覆盖 AI 通过未写 ApprovalDate 的情况） */
    resolveApprovalActionTimeFromStatus(status) {
      if (!status) return null;
      return status.rejectDate
        || status.RejectDate
        || status.approvalDate
        || status.ApprovalDate
        || status.approvalUpdatedDate
        || status.ApprovalUpdatedDate
        || status.approvalCreatedDate
        || status.ApprovalCreatedDate
        || null;
    },
    /** 详情历史：取最近一条 A/R 的审批时间 */
    resolveApprovalActionTimeFromDetail(detail) {
      const history = detail?.approvalHistory || [];
      for (let i = history.length - 1; i >= 0; i -= 1) {
        const row = history[i];
        const code = String(row?.statusCode ?? row?.StatusCode ?? '').trim().toUpperCase();
        if (code !== 'A' && code !== 'R') continue;
        return row?.rejectDate
          ?? row?.RejectDate
          ?? row?.approvalDate
          ?? row?.ApprovalDate
          ?? row?.createdDate
          ?? row?.CreatedDate
          ?? null;
      }
      const header = detail?.approvalHeader;
      return header?.updatedDate ?? header?.UpdatedDate ?? header?.createdDate ?? header?.CreatedDate ?? null;
    },
    resolveApprovalRejectedTime(detail) {
      const history = detail?.approvalHistory || [];
      for (let i = history.length - 1; i >= 0; i -= 1) {
        const row = history[i];
        const rejectDate = row?.rejectDate ?? row?.RejectDate;
        if (rejectDate) return rejectDate;
        const code = String(row?.statusCode ?? row?.StatusCode ?? '').trim().toUpperCase();
        if (code === 'R') {
          return row?.approvalDate ?? row?.ApprovalDate ?? row?.createdDate ?? row?.CreatedDate ?? null;
        }
      }
      const header = detail?.approvalHeader;
      if (header?.updatedDate || header?.UpdatedDate) return header.updatedDate ?? header.UpdatedDate;
      return null;
    },
    resolveOverallRejectFromHistory(detail) {
      const history = detail?.approvalHistory || [];
      for (let i = history.length - 1; i >= 0; i -= 1) {
        const row = history[i];
        const code = String(row?.statusCode ?? row?.StatusCode ?? '').trim().toUpperCase();
        const reason = row?.rejectReason ?? row?.RejectReason ?? '';
        if (code === 'R' || reason) {
          return {
            reason: reason || '',
            by: resolveApprovalRejectByActor(row),
          };
        }
      }
      return { reason: '', by: '' };
    },
    getOverallRejectReason() {
      return (this.overallRejectReasonDraft || '').trim();
    },
    isOverallRejectActive() {
      const panel = this.$refs.overallRejectPanel;
      if (panel && typeof panel.isRejectActive === 'function') {
        return panel.isRejectActive();
      }
      return false;
    },
    getRejectUsageTypesByPage(page) {
      return COMPANY_REJECT_USAGE_TYPES_BY_TAB[page] || [];
    },
    getCurrentTabRejectUsageTypes() {
      const tab = this.TabList?.[this.TabIndex];
      const page = tab?.ProgressPage || '';
      return this.getRejectUsageTypesByPage(page);
    },
    buildFieldRejectsForSave(scope = 'current') {
      const rows = this.approvalContentResults || [];
      const allowTypes = scope === 'all' ? null : this.getCurrentTabRejectUsageTypes();
      const allowSet = allowTypes ? new Set(allowTypes.map((x) => String(x).trim())) : null;

      return rows
        .map((result) => {
          const id = Number(result.approvalRequestResultId) || 0;
          if (id <= 0) return null;
          const usage = String(result.usageType || '').trim();
          if (allowSet && !allowSet.has(usage)) return null;
          const selected = isCompanyApprovalResultRadioSelected(result.resultCode);
          const resultCode = resolveCompanyApprovalResultCodeForSave(result.resultCode);
          return {
            approvalRequestResultId: id,
            rejectedReason: selected ? (this.getProductApprovalResultDraft(result) || '') : '',
            resultCode,
          };
        })
        .filter(Boolean);
    },
    /**
     * 驳回内容相对上次落库是否有改动（总驳回文案 / 子项勾选 / 子项原因）
     * 与供应商 dirty 一致：无改动则切 Tab 不调保存、不提示
     */
    hasApprovalRejectChanges(scope = 'current') {
      const overallNow = this.getOverallRejectReason();
      const overallSaved = String(this.approvalRejectReason || '').trim();
      if (overallNow !== overallSaved) return true;

      const fieldRejects = this.buildFieldRejectsForSave(scope);
      for (let i = 0; i < fieldRejects.length; i += 1) {
        const item = fieldRejects[i];
        const row = (this.approvalContentResults || []).find(
          (x) => Number(x.approvalRequestResultId) === Number(item.approvalRequestResultId),
        );
        if (!row) continue;
        const key = this.getCompanyApprovalResultDraftKey(row);
        const baselineCode = resolveCompanyApprovalResultCodeForSave(
          this.approvalContentResultCodeBaseline[key],
        );
        const nowCode = resolveCompanyApprovalResultCodeForSave(item.resultCode);
        if (baselineCode !== nowCode) return true;

        const wasSelected = isCompanyApprovalResultRadioSelected(baselineCode);
        const savedReason = wasSelected ? String(row.rejectReason || '').trim() : '';
        const nowReason = String(item.rejectedReason || '').trim();
        if (savedReason !== nowReason) return true;
      }
      return false;
    },
    syncApprovalResultCodeBaselineFromRows(rows) {
      const baseline = {};
      (rows || []).forEach((row) => {
        const key = this.getCompanyApprovalResultDraftKey(row);
        if (!key) return;
        baseline[key] = resolveCompanyApprovalResultCodeForSave(row.resultCode);
      });
      this.approvalContentResultCodeBaseline = baseline;
    },
    async saveApprovalRejectContent(options = {}) {
      const silent = !!options.silent;
      const scope = options.scope === 'all' ? 'all' : 'current';
      if (!this.fromAdmin || !this.isApprovalMode) return false;
      if (!this.companyid) return false;

      const fieldRejects = this.buildFieldRejectsForSave(scope);

      try {
        const res = await CompanyInfoService.UpdateCompanyOverallRejectReason({
          empCompanyId: Number(this.companyid) || 0,
          rejectReason: this.getOverallRejectReason(),
          fieldRejects,
        });
        if (!res?.isSuccessful) {
          if (!silent) {
            this.$message({
              message: this.$t('lbl_EMPBE_ProductInfo_ApprovalSavePartialFail'),
              type: 'warning',
            });
          }
          return false;
        }

        this.approvalRejectReason = this.overallRejectReasonDraft || '';
        fieldRejects.forEach((item) => {
          const idx = this.approvalContentResults.findIndex(
            (x) => Number(x.approvalRequestResultId) === Number(item.approvalRequestResultId),
          );
          if (idx >= 0) {
            const code = String(item.resultCode || COMPANY_APPROVAL_RESULT_CODE_UNSELECTED).trim()
              || COMPANY_APPROVAL_RESULT_CODE_UNSELECTED;
            this.$set(this.approvalContentResults, idx, {
              ...this.approvalContentResults[idx],
              rejectReason: item.rejectedReason || '',
              resultCode: code === COMPANY_APPROVAL_RESULT_CODE_SELECTED || code === '0'
                ? COMPANY_APPROVAL_RESULT_CODE_SELECTED
                : COMPANY_APPROVAL_RESULT_CODE_UNSELECTED,
            });
            this.setProductApprovalResultDraft(this.approvalContentResults[idx], item.rejectedReason || '');
            const key = this.getCompanyApprovalResultDraftKey(this.approvalContentResults[idx]);
            if (key) {
              this.$set(
                this.approvalContentResultCodeBaseline,
                key,
                resolveCompanyApprovalResultCodeForSave(item.resultCode),
              );
            }
          }
        });

        if (!silent) {
          this.$showEmpSuccessToast(this.$t('lbl_EMPBE_ProductInfo_ApprovalSaveSuccess'));
        }
        return true;
      } catch (e) {
        if (!silent) {
          this.$message({
            message: this.$t('lbl_EMPBE_ProductInfo_ApprovalSavePartialFail'),
            type: 'warning',
          });
        }
        return false;
      }
    },
    /**
     * @returns {{ ok: boolean, statusCode?: 'R'|'A' }}
     */
    async finishCompanyApproval() {
      if (!this.fromAdmin || !this.isApprovalMode) return { ok: false };
      if (!this.companyid) return { ok: false };

      if (!(await this.ensureApprovalNotStale())) {
        return { ok: false };
      }

      await this.saveApprovalRejectContent({ silent: true, scope: 'all' });

      const isReject = this.isOverallRejectActive();
      let blocked = false;
      let fieldsMissing = false;

      if (!(await this.validateSelectedFieldRejectReasons())) {
        blocked = true;
        fieldsMissing = true;
      }

      if (isReject) {
        const overall = this.getOverallRejectReason();
        if (!overall) {
          const panel = this.$refs.overallRejectPanel;
          const msg = this.$t('lbl_EMPBE_ProductInfo_OverallReject_Placeholder');
          if (panel && typeof panel.showReasonError === 'function') {
            panel.showReasonError(msg, { scroll: !fieldsMissing });
          } else if (!fieldsMissing && panel && typeof panel.focusReason === 'function') {
            panel.focusReason();
          }
          blocked = true;
        }
        if (blocked) return { ok: false };
        const ok = await this.confirmAndSubmitCompanyApproval('R', overall);
        return { ok: !!ok, statusCode: 'R' };
      }

      if (blocked) return { ok: false };
      const ok = await this.confirmAndSubmitCompanyApproval('A', '');
      return { ok: !!ok, statusCode: 'A' };
    },
    getMissingFieldRejectReasons() {
      return (this.approvalContentResults || []).filter((result) => {
        if (!isCompanyApprovalResultRadioSelected(result.resultCode)) return false;
        const reason = String(this.getCompanyApprovalResultDraft(result) || '').trim();
        return !reason;
      });
    },
    getPageForRejectUsageType(usageType) {
      const usage = String(usageType || '').trim();
      if (!usage) return '';
      const pages = Object.keys(COMPANY_REJECT_USAGE_TYPES_BY_TAB);
      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        if ((COMPANY_REJECT_USAGE_TYPES_BY_TAB[page] || []).includes(usage)) return page;
      }
      return '';
    },
    getFieldRejectErrorMessage(result) {
      const key = this.getCompanyApprovalResultDraftKey(result);
      if (!key) return '';
      return this.fieldRejectErrorByKey[key] || '';
    },
    clearFieldRejectError(result) {
      const key = this.getCompanyApprovalResultDraftKey(result);
      if (!key || !this.fieldRejectErrorByKey[key]) return;
      this.$delete(this.fieldRejectErrorByKey, key);
    },
    clearAllFieldRejectErrors() {
      this.fieldRejectErrorByKey = {};
    },
    async validateSelectedFieldRejectReasons() {
      const missing = this.getMissingFieldRejectReasons();
      if (!missing.length) {
        this.clearAllFieldRejectErrors();
        return true;
      }
      const msg = this.$t('lbl_EMPBE_ProductInfo_OverallReject_Placeholder');
      const errors = {};
      missing.forEach((row) => {
        const key = this.getCompanyApprovalResultDraftKey(row);
        if (key) errors[key] = msg;
      });
      this.fieldRejectErrorByKey = errors;

      const firstKey = this.getCompanyApprovalResultDraftKey(missing[0]);
      const firstPage = this.getPageForRejectUsageType(missing[0].usageType);
      if (firstPage && Array.isArray(this.TabList)) {
        const idx = this.TabList.findIndex((t) => t.ProgressPage === firstPage);
        if (idx >= 0 && idx !== this.TabIndex) {
          this.TabIndex = idx;
          if (typeof this.selectTab === 'function') {
            this.selectTab(idx);
          }
        }
      }
      await this.scrollToFieldRejectError(firstKey);
      return false;
    },
    async scrollToFieldRejectError(resultKey) {
      const key = String(resultKey || '').trim();
      if (!key) return;
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 150));
      const root = this.$el;
      if (!root || typeof root.querySelector !== 'function') return;
      const safeKey = key.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const el = root.querySelector(`[data-approval-result-key="${safeKey}"]`);
      if (!el) return;
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      const input = el.querySelector('textarea.frf__input, .frf__input');
      if (input && typeof input.focus === 'function') {
        setTimeout(() => input.focus(), 180);
      }
    },
    confirmAndSubmitCompanyApproval(statusCode, rejectReason) {
      const isReject = String(statusCode).toUpperCase() === 'R';
      return this.$swal({
        title: this.$t(
          isReject
            ? 'lbl_EMPBE_CompanyInfo_ConfirmRejectTitle'
            : 'lbl_EMPBE_CompanyInfo_ConfirmApproveTitle',
        ),
        html: buildApprovalConfirmDialogBody(this, {
          isReject,
          currentStatusCode: this.approvalStatusCode,
        }),
        showCancelButton: true,
        confirmButtonText: this.$t(
          isReject
            ? 'lbl_EMPBE_ProductInfo_ConfirmRejectOk'
            : 'lbl_EMPBE_ProductInfo_ConfirmApproveOk',
        ),
        cancelButtonText: this.$t('lbl_EMPBE_Cancel'),
        customClass: isReject ? 'AlertDialog-DEL' : 'AlertDialog-SaveSucess AlertDialog-Approve',
        reverseButtons: true,
        confirmButtonColor: isReject ? '#DC3545' : '#51BB25',
      }).then(async (res) => {
        if (!res.isConfirmed) return false;
        if (!(await this.ensureApprovalNotStale())) {
          return false;
        }
        try {
          const apiRes = await CompanyInfoService.CompanyApprove({
            companyIds: String(this.companyid),
            statusCode: isReject ? 'R' : 'A',
            rejectReason: rejectReason || '',
            remark: '',
          });
          if (apiRes?.isSuccessful) {
            this.$showEmpSuccessToast(this.$t(
              isReject
                ? 'lbl_EMPBE_ProductInfo_RejectSuccess'
                : 'lbl_EMPBE_ProductInfo_ApproveSuccess',
            ));
            await this.LoadApprovalStatus();
            this.$emit('approval-finished', {
              statusCode: isReject ? 'R' : 'A',
              empCompanyId: this.companyid,
            });
            return true;
          }
          this.$message({
            message: (apiRes?.systemErrorMsg && apiRes.systemErrorMsg.join('<br>'))
              || this.$t('lbl_EMPBE_ProductInfo_ApprovalActionFail'),
            type: 'error',
            dangerouslyUseHTMLString: true,
          });
          return false;
        } catch (e) {
          this.$message({
            message: this.$t('lbl_EMPBE_ProductInfo_ApprovalActionFail'),
            type: 'error',
          });
          return false;
        }
      });
    },
    async loadOverallRejectTemplates() {
      if (this.overallRejectTemplates.length) return;
      try {
        const res = await adminService.GetRejectReasons();
        if (!res?.isSuccessful || !res.data) return;
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.reasons)
            ? res.data.reasons
            : Array.isArray(res.data.rejectReasons)
              ? res.data.rejectReasons
              : [];
        // 与产品一致：FieldRejectionFeedback 需要 { id, label }
        this.overallRejectTemplates = raw
          .map((item, idx) => {
            if (typeof item === 'string') {
              const label = String(item).trim();
              return label ? { id: `ovr_${idx}`, label } : null;
            }
            if (!item || typeof item !== 'object') return null;
            const label = item.RejectReason || item.rejectReason
              || item.RejectTitle || item.rejectTitle
              || item.reason || item.Reason || '';
            if (!String(label).trim()) return null;
            return { id: `ovr_${idx}`, label: String(label).trim() };
          })
          .filter(Boolean);
      } catch (e) {
        // 模板可选，失败时仅隐藏 Template 下拉
        console.error('loadOverallRejectTemplates', e);
      }
    },
    getProductApprovalResultsByUsageType(usageTypes) {
      const types = Array.isArray(usageTypes) ? usageTypes : [usageTypes];
      const set = new Set(types.map((x) => String(x || '').trim()).filter(Boolean));
      return (this.approvalContentResults || []).filter((row) =>
        set.has(String(row.usageType || '').trim()));
    },
    getCompanyApprovalResultsByUsageType(usageTypes) {
      return this.getProductApprovalResultsByUsageType(usageTypes);
    },
    getCompanyApprovalResultDraftKey(result) {
      if (!result) return '';
      const approvalRequestResultId = Number(result.approvalRequestResultId) || 0;
      if (approvalRequestResultId > 0) return String(approvalRequestResultId);
      const usageId = result.usageId;
      if (usageId != null && usageId !== '') return `usage_id_${usageId}`;
      const usageType = String(result.usageType || '').trim();
      if (usageType) return `usage_${usageType}`;
      return '';
    },
    getProductApprovalResultDraft(result) {
      const key = this.getCompanyApprovalResultDraftKey(result);
      if (!key) return '';
      if (Object.prototype.hasOwnProperty.call(this.approvalContentResultDrafts, key)) {
        return this.approvalContentResultDrafts[key];
      }
      return result?.rejectReason || '';
    },
    getCompanyApprovalResultDraft(result) {
      return this.getProductApprovalResultDraft(result);
    },
    setProductApprovalResultDraft(result, value) {
      const key = this.getCompanyApprovalResultDraftKey(result);
      if (!key) return;
      this.$set(this.approvalContentResultDrafts, key, value);
      if (String(value || '').trim()) {
        this.clearFieldRejectError(result);
      }
    },
    setCompanyApprovalResultDraft(result, value) {
      this.setProductApprovalResultDraft(result, value);
    },
    setProductApprovalResultCode(result, resultCode) {
      const key = this.getCompanyApprovalResultDraftKey(result);
      if (!key) return;
      const code = resolveCompanyApprovalResultCodeForSave(resultCode);
      const selected = isCompanyApprovalResultRadioSelected(code);
      const idx = this.approvalContentResults.findIndex(
        (x) => this.getCompanyApprovalResultDraftKey(x) === key,
      );
      if (idx >= 0) {
        this.$set(this.approvalContentResults, idx, {
          ...this.approvalContentResults[idx],
          resultCode: code,
        });
      }
      if (!selected) {
        this.clearFieldRejectError(result);
      }
      // 子项勾选时联动勾上总驳回（可被用户再手动取消）
      if (selected) {
        const panel = this.$refs.overallRejectPanel;
        if (panel && typeof panel.ensureActive === 'function') {
          panel.ensureActive();
        }
      }
      this.refreshTabExclamationMarks();
    },
    setCompanyApprovalResultCode(result, resultCode) {
      this.setProductApprovalResultCode(result, resultCode);
    },
    tabHasRejectedFields(page) {
      const types = this.getRejectUsageTypesByPage(page);
      if (!types.length) return false;
      return this.getProductApprovalResultsByUsageType(types).some((row) =>
        isVisibleApprovalRejectRow(row, this),
      );
    },
    /**
     * 左侧感叹号：未填 或 字段驳回。
     * 审批模式：驳回只看 resultCode；编辑/供应商：还需非空驳回文案（与媒体驳回框一致）。
     */
    refreshTabExclamationMarks() {
      if (!this.TabList || !this.TabList.length) return;
      const showRejectMark = shouldIncludeRejectHintsInTodo(this);
      this.TabList.forEach((item, idx) => {
        if (!item.ProgressPage) return;
        const hasUnfilled = !!item._hasUnfilledRequired;
        const hasReject = showRejectMark && this.tabHasRejectedFields(item.ProgressPage);
        this.$set(this.TabList[idx], '_hasRejectedFields', hasReject);
        this.$set(this.TabList[idx], 'status', (hasUnfilled || hasReject) ? 1 : 0);
      });
    },
  },
};
